import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import React, { useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import * as yup from "yup";
import Loader from "../../components/Loader/Loader";
import { deleteAccount } from "../../services/services";
import "./delete-account.css";

const DeleteAccount = () => {
  const [completed, setCompleted] = useState(false);
  const mutation = useMutation({ mutationFn: deleteAccount, onSuccess: () => setCompleted(true) });
  const formik = useFormik({
    initialValues: { email: "", password: "", reason: "", confirmation: false },
    validationSchema: yup.object({
      email: yup.string().trim().email("Enter a valid email address").required("Email is required"),
      password: yup.string().required("Password is required"),
      reason: yup.string().trim().max(1000, "Reason must be 1,000 characters or fewer"),
      confirmation: yup.boolean().oneOf([true], "Confirm that you understand this action").required(),
    }),
    onSubmit: (values) => mutation.mutate({
      email: values.email.trim(),
      password: values.password,
      reason: values.reason.trim(),
      confirmation: values.confirmation,
    }),
  });

  if (completed) {
    return (
      <main className="delete-account-page">
        <Container className="delete-account-container">
          <div className="delete-account-card delete-account-success" role="status">
            <div className="success-mark" aria-hidden="true">✓</div>
            <p className="delete-account-eyebrow">Request completed</p>
            <h1>Your account has been deleted</h1>
            <p>Access has been disabled immediately. Associated personal data will be removed according to our Privacy Policy and applicable retention requirements.</p>
            <Link className="delete-account-home" to="/">Return to sign in</Link>
          </div>
        </Container>
      </main>
    );
  }

  return (
    <main className="delete-account-page">
      <Container className="delete-account-container">
        <Row className="g-0 delete-account-card">
          <Col lg={5} className="delete-account-info">
            <Link to="/" className="delete-account-logo" aria-label="Prep Guardian home">
              <img src="/images/logo.svg" alt="Prep Guardian" />
            </Link>
            <p className="delete-account-eyebrow">Account settings</p>
            <h1>Delete your Prep Guardian account</h1>
            <p>Use this form to permanently close your account. Your access will be disabled as soon as the request succeeds.</p>
            <div className="delete-account-note">
              <strong>Before you continue</strong>
              <ul>
                <li>You will be signed out on every device.</li>
                <li>Your profile and associated personal information will be deleted.</li>
                <li>Some records may be retained when required by law.</li>
              </ul>
            </div>
          </Col>

          <Col lg={7} className="delete-account-form-panel">
            <div className="delete-account-form-heading">
              <p className="delete-account-eyebrow">Identity verification</p>
              <h2>Confirm account deletion</h2>
              <p>Enter the credentials you use to sign in.</p>
            </div>
            <Form noValidate onSubmit={formik.handleSubmit}>
              <Form.Group className="mb-4" controlId="delete-account-email">
                <Form.Label>Email address</Form.Label>
                <Form.Control type="email" name="email" autoComplete="email" placeholder="name@example.com" value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur} isInvalid={formik.touched.email && Boolean(formik.errors.email)} />
                <Form.Control.Feedback type="invalid">{formik.errors.email}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-4" controlId="delete-account-password">
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" name="password" autoComplete="current-password" placeholder="Enter your password" value={formik.values.password} onChange={formik.handleChange} onBlur={formik.handleBlur} isInvalid={formik.touched.password && Boolean(formik.errors.password)} />
                <Form.Control.Feedback type="invalid">{formik.errors.password}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-4" controlId="delete-account-reason">
                <Form.Label>Reason for leaving <span>(optional)</span></Form.Label>
                <Form.Control as="textarea" rows={3} name="reason" placeholder="Tell us how we could improve" value={formik.values.reason} onChange={formik.handleChange} onBlur={formik.handleBlur} isInvalid={formik.touched.reason && Boolean(formik.errors.reason)} />
                <Form.Control.Feedback type="invalid">{formik.errors.reason}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="delete-account-confirm mb-4" controlId="delete-account-confirmation">
                <Form.Check name="confirmation" checked={formik.values.confirmation} onChange={formik.handleChange} onBlur={formik.handleBlur} isInvalid={formik.touched.confirmation && Boolean(formik.errors.confirmation)} label="I understand that deleting my account is permanent and cannot be undone." feedback={formik.errors.confirmation} feedbackType="invalid" />
              </Form.Group>
              <Button className="delete-account-submit" type="submit" disabled={mutation.isPending}>Delete my account</Button>
            </Form>
            <p className="delete-account-support">Need help? Email <a href="mailto:support@prepguardian.com">support@prepguardian.com</a></p>
            <div className="delete-account-links">
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/service-agreement">Service Agreement</Link>
            </div>
          </Col>
        </Row>
      </Container>
      {mutation.isPending && <Loader />}
    </main>
  );
};

export default DeleteAccount;
